import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { DataSources } from '@losan/core';
import { App } from './App.js';
import { SourcesProvider } from './contexts/SourcesContext.js';
import { IdentityProvider, type Identity } from './contexts/IdentityContext.js';
import { PostMessageBridge } from './PostMessageBridge.js';
import { createBrowserSnapshotSources } from './sources/browserSnapshotSources.js';
import './styles.css';

export interface MountOptions {
  /** Identity injected by the host portal. If omitted, the dashboard renders
   * its own login fallback. */
  identity?: Identity | null;
  /** Custom data sources. Defaults to mock (the only safe browser default —
   * GAREM credentials must never ship to the client). The host portal should
   * inject its own real sources via a server-side proxy when integrating. */
  sources?: DataSources;
  /** Enable the postMessage bridge for iframe embedding. The dashboard will
   * accept set-identity / clear-identity commands from this origin and emit
   * ready / height / identity-changed events back. Use a specific origin in
   * production; "*" is dev-only. */
  postMessage?: { allowedOrigin: string };
}

export interface DashboardHandle {
  /** Unmount the dashboard and free React resources. */
  unmount(): void;
}

/**
 * Embeds the dashboard into a host element. Designed to be called both from
 * a standalone HTML shell (this app's main.tsx) and from the Portal del Agente.
 *
 *   mount(document.getElementById('root')!, { identity: { agentId: 'a-1' } });
 */
export function mount(element: HTMLElement, opts: MountOptions = {}): DashboardHandle {
  // Default: real LOSAN data from JSON snapshots extracted from Power BI.
  // The host portal can still inject custom sources (e.g. a server-side proxy)
  // by passing them via `opts.sources`.
  const sources = opts.sources ?? createBrowserSnapshotSources();
  const queryClient = new QueryClient({
    defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
  });

  const root: Root = createRoot(element);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <SourcesProvider value={sources}>
          <IdentityProvider initial={opts.identity ?? null}>
            {opts.postMessage && <PostMessageBridge allowedOrigin={opts.postMessage.allowedOrigin} />}
            <App />
          </IdentityProvider>
        </SourcesProvider>
      </QueryClientProvider>
    </StrictMode>,
  );

  return { unmount: () => root.unmount() };
}
